import React, { useCallback, useEffect, useState } from 'react';
import {
  CheckCircle2,
  Circle,
  ListTodo,
  Loader2,
  RefreshCw,
  SkipForward,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '../../lib/supabase';
import {
  isOpenTaskStatus,
  taskStatusUpdatePatch,
  taskTypeLabel,
  type NetworkingTaskRow,
  type NetworkingTaskStatus,
} from '../../lib/networkingTasks';
import TranscriptTaskDetailModal from './TranscriptTaskDetailModal';
import ConfirmTranscriptTaskStatusModal from './ConfirmTranscriptTaskStatusModal';
import { HoverTooltip } from '../Common/HoverTooltip';

type PendingStatusChange = {
  task: NetworkingTaskRow;
  nextStatus: NetworkingTaskStatus;
};

type ExtractionStatus = 'none' | 'pending' | 'ready' | 'error';

interface TranscriptExtractedTasksPanelProps {
  transcriptId: string;
  className?: string;
  onTasksChanged?: () => void;
}

function formatDue(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function sortTranscriptTasks(rows: NetworkingTaskRow[]): NetworkingTaskRow[] {
  return [...rows].sort((a, b) => {
    if (a.task_type === 'transcript_review' && b.task_type !== 'transcript_review') return -1;
    if (b.task_type === 'transcript_review' && a.task_type !== 'transcript_review') return 1;
    return new Date(a.due_at).getTime() - new Date(b.due_at).getTime();
  });
}

const STATUS_DOT: Record<string, string> = {
  pending: 'bg-amber-400',
  in_progress: 'bg-sky-400',
  done: 'bg-emerald-400',
  skipped: 'bg-gray-400',
  canceled: 'bg-gray-400',
};

const TASK_ACTION_TITLES = {
  start: 'Start — mark in progress',
  startDetail: 'You are working on this follow-up',
  done: 'Done — mark complete',
  doneDetail: 'This follow-up is finished',
  skip: 'Skip',
  skipDetail: 'Not actionable — remove from your open queue',
  viewDetails: 'View full task details',
  preview: 'Preview tasks AI would create (does not save)',
  extract: 'Extract follow-up tasks from this transcript with AI',
} as const;

const TranscriptExtractedTasksPanel: React.FC<TranscriptExtractedTasksPanelProps> = ({
  transcriptId,
  className = '',
  onTasksChanged,
}) => {
  const [tasks, setTasks] = useState<NetworkingTaskRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [extractionStatus, setExtractionStatus] = useState<ExtractionStatus>('none');
  const [detailTask, setDetailTask] = useState<NetworkingTaskRow | null>(null);
  const [pendingChange, setPendingChange] = useState<PendingStatusChange | null>(null);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    if (!transcriptId.trim()) {
      setTasks([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [tasksRes, transcriptRes] = await Promise.all([
        supabase
          .from('tasks')
          .select('*')
          .eq('transcript_id', transcriptId)
          .order('due_at', { ascending: true }),
        supabase
          .from('transcripts')
          .select('tasks_extraction_status')
          .eq('id', transcriptId)
          .maybeSingle(),
      ]);

      if (tasksRes.error) throw tasksRes.error;
      const sorted = sortTranscriptTasks((tasksRes.data ?? []) as NetworkingTaskRow[]);
      setTasks(sorted);
      setDetailTask((prev) => {
        if (!prev) return prev;
        return sorted.find((t) => t.id === prev.id) ?? prev;
      });

      if (transcriptRes.data) {
        setExtractionStatus(
          (transcriptRes.data as { tasks_extraction_status?: ExtractionStatus }).tasks_extraction_status ??
            'none',
        );
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to load tasks');
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [transcriptId]);

  useEffect(() => {
    void load();
  }, [load]);

  const runExtraction = async (dryRun: boolean) => {
    setExtracting(true);
    try {
      const { data, error } = await supabase.functions.invoke<{
        success?: boolean;
        error?: string;
        created_count?: number;
        skipped_count?: number;
        proposed_count?: number;
      }>('extract-transcript-tasks', {
        body: { transcript_id: transcriptId, dry_run: dryRun },
      });

      if (error) throw new Error(error.message || 'Extraction request failed');
      if (data?.error) throw new Error(data.error);

      if (dryRun) {
        const n = data?.proposed_count ?? 0;
        toast.message(
          n > 0 ? `Preview: ${n} task(s) would be created.` : 'Preview: no new follow-ups found.',
        );
      } else {
        const created = data?.created_count ?? 0;
        const skipped = data?.skipped_count ?? 0;
        if (created > 0) {
          toast.success(`Created ${created} task(s)${skipped > 0 ? ` · ${skipped} skipped (dupes)` : ''}`);
        } else if (skipped > 0) {
          toast.message(`${skipped} task(s) already exist`);
        } else {
          toast.message('No new follow-up tasks found');
        }
      }

      await load();
      onTasksChanged?.();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Task extraction failed');
    } finally {
      setExtracting(false);
    }
  };

  const applyStatusChange = async (id: string, status: NetworkingTaskStatus) => {
    setBusyId(id);
    try {
      const { error } = await supabase
        .from('tasks')
        .update(taskStatusUpdatePatch(status))
        .eq('id', id);
      if (error) throw error;
      if (status === 'done') toast.success('Task marked done');
      else if (status === 'skipped') toast.success('Task skipped');
      else if (status === 'in_progress') toast.success('Task in progress');
      else if (status === 'pending') toast.success('Task reopened');
      await load();
      onTasksChanged?.();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  const requestStatusChange = (task: NetworkingTaskRow, nextStatus: NetworkingTaskStatus) => {
    setPendingChange({ task, nextStatus });
  };

  const confirmStatusChange = async () => {
    if (!pendingChange) return;
    setConfirming(true);
    try {
      await applyStatusChange(pendingChange.task.id, pendingChange.nextStatus);
      if (
        pendingChange.nextStatus === 'done' ||
        pendingChange.nextStatus === 'skipped'
      ) {
        setDetailTask((prev) => (prev?.id === pendingChange.task.id ? null : prev));
      }
      setPendingChange(null);
    } finally {
      setConfirming(false);
    }
  };

  const openCount = tasks.filter((t) => isOpenTaskStatus(t.status)).length;

  return (
    <section
      className={`rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 p-5 flex flex-col min-h-[280px] ${className}`}
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
          <ListTodo className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
          Tasks
          {openCount > 0 ? (
            <span className="font-normal text-xs text-gray-500 dark:text-gray-400">({openCount} open)</span>
          ) : null}
        </h2>
        <div className="flex items-center gap-1.5 shrink-0">
          <HoverTooltip label={TASK_ACTION_TITLES.preview}>
            <button
              type="button"
              disabled={extracting}
              onClick={() => void runExtraction(true)}
              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              aria-label={TASK_ACTION_TITLES.preview}
            >
              {extracting ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Preview
            </button>
          </HoverTooltip>
          <HoverTooltip label={TASK_ACTION_TITLES.extract}>
            <button
              type="button"
              disabled={extracting || extractionStatus === 'pending'}
              onClick={() => void runExtraction(false)}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-md bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
              aria-label={TASK_ACTION_TITLES.extract}
            >
              {extracting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              Extract tasks
            </button>
          </HoverTooltip>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-8">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading…
        </div>
      ) : tasks.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-4">
          No tasks yet. Use <strong>Extract tasks</strong> to pull follow-ups from this call.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700/80 -mx-1">
          {tasks.map((task) => {
            const open = isOpenTaskStatus(task.status);
            const busy = busyId === task.id;
            const dot = STATUS_DOT[task.status] ?? 'bg-gray-400';

            return (
              <li
                key={task.id}
                className="flex items-center gap-2 px-1 py-2.5 group"
              >
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dot}`} title={task.status} />
                <button
                  type="button"
                  onClick={() => setDetailTask(task)}
                  title={TASK_ACTION_TITLES.viewDetails}
                  className="min-w-0 flex-1 text-left rounded-md -mx-1 px-1 py-0.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <p className="text-sm text-gray-900 dark:text-white truncate leading-snug">{task.title}</p>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                    {taskTypeLabel(task.task_type)}
                    {task.due_at ? ` · Due ${formatDue(task.due_at)}` : ''}
                  </p>
                </button>
                {open ? (
                  <div className="flex items-center gap-0.5 shrink-0 opacity-80 group-hover:opacity-100">
                    {task.status === 'pending' ? (
                      <HoverTooltip
                        label={
                          <>
                            <span className="block font-semibold">{TASK_ACTION_TITLES.start}</span>
                            <span className="block font-normal text-gray-300">{TASK_ACTION_TITLES.startDetail}</span>
                          </>
                        }
                      >
                        <button
                          type="button"
                          disabled={busy}
                          onClick={(e) => {
                            e.stopPropagation();
                            requestStatusChange(task, 'in_progress');
                          }}
                          className="p-1 rounded text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-950/40 disabled:opacity-50"
                          aria-label={`${TASK_ACTION_TITLES.start}. ${TASK_ACTION_TITLES.startDetail}`}
                        >
                          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Circle className="w-3.5 h-3.5" />}
                        </button>
                      </HoverTooltip>
                    ) : null}
                    <HoverTooltip
                      label={
                        <>
                          <span className="block font-semibold">{TASK_ACTION_TITLES.done}</span>
                          <span className="block font-normal text-gray-300">{TASK_ACTION_TITLES.doneDetail}</span>
                        </>
                      }
                    >
                      <button
                        type="button"
                        disabled={busy}
                        onClick={(e) => {
                          e.stopPropagation();
                          requestStatusChange(task, 'done');
                        }}
                        className="p-1 rounded text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/40 disabled:opacity-50"
                        aria-label={`${TASK_ACTION_TITLES.done}. ${TASK_ACTION_TITLES.doneDetail}`}
                      >
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                      </button>
                    </HoverTooltip>
                    <HoverTooltip
                      label={
                        <>
                          <span className="block font-semibold">{TASK_ACTION_TITLES.skip}</span>
                          <span className="block font-normal text-gray-300">{TASK_ACTION_TITLES.skipDetail}</span>
                        </>
                      }
                    >
                      <button
                        type="button"
                        disabled={busy}
                        onClick={(e) => {
                          e.stopPropagation();
                          requestStatusChange(task, 'skipped');
                        }}
                        className="p-1 rounded text-gray-500 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                        aria-label={`${TASK_ACTION_TITLES.skip}. ${TASK_ACTION_TITLES.skipDetail}`}
                      >
                        <SkipForward className="w-3.5 h-3.5" />
                      </button>
                    </HoverTooltip>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      <ConfirmTranscriptTaskStatusModal
        task={pendingChange?.task ?? null}
        nextStatus={pendingChange?.nextStatus ?? null}
        isOpen={pendingChange != null}
        busy={confirming}
        onCancel={() => setPendingChange(null)}
        onConfirm={() => void confirmStatusChange()}
      />

      <TranscriptTaskDetailModal
        task={detailTask}
        isOpen={detailTask != null}
        onClose={() => setDetailTask(null)}
        onUpdated={() => {
          void load();
          onTasksChanged?.();
        }}
        onRequestStatusChange={(task, nextStatus) => requestStatusChange(task, nextStatus)}
      />
    </section>
  );
};

export default TranscriptExtractedTasksPanel;
